import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Wind } from 'lucide-react';

// Generador de sonidos
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

const playTone = (audioContext, frequency, duration, type = 'sine') => {
  if (!audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = frequency;
  oscillator.type = type;
  
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

// Sonido de finalización de apnea
const playApneaCompleteSound = (audioContext) => {
  if (!audioContext) return;
  
  const notes = [392, 523, 659]; // Sol4, Do5, Mi5
  notes.forEach((freq, i) => {
    setTimeout(() => {
      playTone(audioContext, freq, 0.3, 'sine');
    }, i * 150);
  });
};

export default function ApneaStage({ onComplete }) {
  const [seconds, setSeconds] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Initialize audio context
    audioContextRef.current = createAudioContext();
    
    // Start timer
    intervalRef.current = setInterval(() => {
      setSeconds(prev => prev + 1);
    }, 1000);

    // Listen for spacebar press
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && isActive && !isComplete) {
        e.preventDefault();
        handleFinishApnea();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, isComplete]);

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFinishApnea = () => {
    setIsActive(false);
    setIsComplete(true);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    if (audioContextRef.current) {
      playApneaCompleteSound(audioContextRef.current);
    }
    
    // Wait a moment before proceeding
    setTimeout(() => {
      onComplete?.(seconds);
    }, 1500);
  };

  if (isComplete) {
    return (
      <div className="flex flex-col items-center justify-center py-10" data-testid="apnea-complete-stage">
        <motion.div
          className="relative w-56 h-56 md:w-72 md:h-72 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-400/30 flex items-center justify-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center z-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <p className="text-4xl mb-3">🌟</p>
            </motion.div>
            <p className="text-xl font-medium text-white mb-2">¡Excelente!</p>
            <p className="text-3xl font-bold text-emerald-400">{formatTime(seconds)}</p>
            <p className="text-sm text-white/60 mt-2">de apnea</p>
          </div>
        </motion.div>
        
        <div className="mt-8 text-center">
          <p className="text-emerald-400 font-medium">Apnea completada</p>
          <p className="text-sm text-white/40 mt-1">Preparándote para canalizar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-10" data-testid="apnea-stage">
      <motion.div
        className="relative w-56 h-56 md:w-72 md:h-72 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden"
        animate={{ 
          boxShadow: ['0 0 20px rgba(139, 92, 246, 0.2)', '0 0 40px rgba(139, 92, 246, 0.4)', '0 0 20px rgba(139, 92, 246, 0.2)']
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        {/* Pulsing inner circle */}
        <motion.div
          className="absolute inset-8 rounded-full bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-400/20"
          animate={{ 
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        <div className="text-center z-10">
          <Wind className="w-10 h-10 text-purple-400 mx-auto mb-3 opacity-50" />
          <p className="text-lg text-white/70 mb-2">Retén el aire</p>
          <p className="text-5xl font-bold text-white" data-testid="apnea-timer">
            {formatTime(seconds)}
          </p>
        </div>
      </motion.div>

      <div className="mt-8 text-center">
        <p className="text-white/70 mb-6">
          Mantén la respiración el tiempo que puedas
        </p>
        
        <button
          onClick={handleFinishApnea}
          data-testid="finish-apnea-button"
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg"
        >
          Terminar Apnea
        </button>
        
        <p className="text-sm text-white/40 mt-4">
          Presiona el botón o la <span className="text-white/60 font-medium">barra espaciadora</span> cuando necesites respirar
        </p>
      </div>
    </div>
  );
}
